import type { EChartsOption } from "echarts";
import * as echarts from "echarts/core";
import React, { useEffect, useRef } from "react";

// Import only the parts you need:
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";

import { BarChart, LineChart, PieChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";

// Register components & renderer
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  BarChart,
  PieChart,
  CanvasRenderer,
]);

type EChartWrapperProps = {
  option: EChartsOption;
  theme?: "light" | "dark" | string;
  style?: React.CSSProperties;
  className?: string;
  onChartReady?: (chart: echarts.EChartsType) => void;
  updateOption?: (chart: echarts.EChartsType) => void;
};

export const EChartWrapper: React.FC<EChartWrapperProps> = ({
  option,
  theme = "light",
  style = { width: "100%", height: "400px" },
  className,
  onChartReady,
  updateOption,
}) => {
  const instanceRef = useRef<echarts.EChartsType | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    // Dispose old chart if needed
    if (instanceRef.current) {
      instanceRef.current.dispose();
    }

    const chart = echarts.init(el, theme, { renderer: "canvas" });
    chart.setOption(option);
    instanceRef.current = chart;
    onChartReady?.(chart);

    if (updateOption) updateOption(chart); // enable external control

    // Resize on container size change
    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
      instanceRef.current = null;
    };
  }, [option, theme])

  return <div ref={chartRef} style={style} className={className} />;
};
